import { Api } from "./helpers/api";
import { LOADER_SVG } from "./helpers/constants";

window.addEventListener('load', () => {
    setupHomeDesignOptionsPanel(document);
});

export function setupHomeDesignOptionsPanel(panel) {
    const homeFloorplanOptionPanels = panel.querySelectorAll('.home-floorplan-option-panel');
    const homeSpecificationsPanel = panel.querySelector('.home-specifications-panel');

    for (const homeFloorplanOptionPanel of homeFloorplanOptionPanels) {
        const switches = homeFloorplanOptionPanel.querySelectorAll('input[data-floorplan-switch]');

        // Helper functions
        const formatNumber = (num) => (num % 1 === 0 ? num.toFixed(0) : num.toFixed(1));
        const updateStatValue = (statEl, newValue) => {
            if (statEl) {
                let finalValue;

                // Check if the new value is NaN and only apply this check for numerical values
                if (typeof newValue === 'number' && isNaN(newValue)) {
                    finalValue = 'n/a'; // Set to "N/A" if the value is NaN
                } else {
                    finalValue = typeof newValue === 'number' ? formatNumber(newValue) : newValue;
                }

                statEl.textContent = finalValue;
            }
        };

        const getMaxSelectedValue = (name, switches, key) => {
            const selectedValues = Array.from(switches)
                .filter(s => s.checked && s.dataset[key])
                .map(s => JSON.parse(s.dataset[key] || '{}')[name])
                .filter(v => v !== undefined);

            return Math.max(...selectedValues, 0);
        };

        // Handle each switch
        switches.forEach((switchEl) => {
            switchEl.addEventListener('change', () => {
                const isChecked = switchEl.checked;
                const adjustmentStats = JSON.parse(switchEl.dataset.adjustmentStats || '{}');
                const largestAdditionStats = JSON.parse(switchEl.dataset.largestAdditionStats || '{}');
                const baseStats = JSON.parse(switchEl.dataset.baseStats || '{}');

                // Update conflicting options based on all active switches
                const updateConflictingOptions = () => {
                    const activeConflicts = new Set();

                    // Gather all active conflicts from checked switches
                    switches.forEach((switchEl) => {
                        if (switchEl.checked) {
                            const conflicts = switchEl.dataset.conflicts.split(',');
                            conflicts.forEach((conflict) => activeConflicts.add(conflict.trim()));
                        }
                    });

                    // Update the disabled state of each switch
                    switches.forEach((switchEl) => {
                        const optionId = switchEl.dataset.optionId;
                        if (activeConflicts.has(optionId)) {
                            switchEl.disabled = true; // Disable if it's in the active conflict set
                        } else {
                            switchEl.disabled = false; // Enable if it's not in the active conflict set
                        }
                    });
                };

                // Initial conflict update on load
                updateConflictingOptions();

                // Toggle overlay images based on data-toggle-overlay and data-overlay
                const toggleOverlayOptionId = switchEl.dataset.optionId;
                if (toggleOverlayOptionId) {
                    const overlayEl = homeFloorplanOptionPanel.querySelector(`[data-overlay-option-id="${toggleOverlayOptionId}"]`);
                    if (overlayEl) {
                        overlayEl.setAttribute('data-active', isChecked.toString()); // Set 'true' or 'false'
                    }
                }

                if (homeSpecificationsPanel) {
                    // Update adjustment stats
                    Object.entries(adjustmentStats).forEach(([name, value]) => {
                        const statEl = homeSpecificationsPanel.querySelector(`[data-value-for="${name}"]`);
                        if (statEl) {
                            // Get the current value from the element's text content or use the original value
                            const currentValue = parseFloat(statEl.textContent || 0);
                            const originalValue = statEl.dataset.originalValue ? parseFloat(statEl.dataset.originalValue) : 0;

                            const currentMax = getMaxSelectedValue(name, switches, 'adjustmentStats');

                            // If no items are selected, revert to the original value
                            const newValue = isChecked ? currentValue + currentMax : (currentMax === 0 ? originalValue : currentValue - currentMax);

                            updateStatValue(statEl, newValue);
                        }
                    });

                    // Update largest addition stats
                    Object.entries(largestAdditionStats).forEach(([stat, value]) => {
                        const statEl = homeSpecificationsPanel.querySelector(`[data-value-for="${stat}"]`);
                        if (statEl) {
                            // Collect all selected values for the stat
                            const selectedValues = Array.from(switches)
                                .filter((s) => s.checked && s.dataset.largestAdditionStats)
                                .map((s) => {
                                    const statData = JSON.parse(s.dataset.largestAdditionStats || '{}');
                                    return statData[stat];
                                })
                                .filter((v) => v !== undefined);

                            const originalValue = statEl.dataset.originalValue ? parseFloat(statEl.dataset.originalValue) : 0;

                            // If no items are selected, revert to the original value
                            if (selectedValues.length === 0) {
                                updateStatValue(statEl, originalValue || 0);
                            } else {
                                const currentMax = Math.max(...selectedValues);
                                const newValue = isChecked ? Math.max(currentMax, value) : currentMax;
                                updateStatValue(statEl, newValue || 0);
                            }
                        }
                    });


                    // Update base stats
                    Object.entries(baseStats).forEach(([stat, value]) => {
                        const statEl = homeSpecificationsPanel.querySelector(`[data-value-for="${stat}"]`);

                        if (statEl) {
                            const selectedValues = Array.from(switches)
                                .filter((s) => s.checked && s.dataset.baseStats)
                                .map((s) => JSON.parse(s.dataset.baseStats || '{}')[stat])
                                .filter((v) => v !== undefined);

                            let originalValue = statEl.dataset.originalValue || 0;
                            if (stat === "Cars") {
                                selectedValues.push(originalValue.trim());
                            } else {
                                selectedValues.push(parseFloat(originalValue));
                            }

                            let newValue;

                            if (stat === "Cars") {
                                if (selectedValues.includes("double") || selectedValues.includes("Double")) {
                                    newValue = "Double";
                                } else if (selectedValues.includes("single") || selectedValues.includes("Single")) {
                                    newValue = "Single";
                                } else {
                                    newValue = "Single";
                                }
                            } else {
                                const numericValues = selectedValues.map((v) => parseFloat(v) || 0);
                                newValue = Math.max(...numericValues);
                            }

                            // Update the spec section with the new value
                            updateStatValue(statEl, newValue);
                        }
                    });
                }
            });
        });

        const downloadPdfButton = homeFloorplanOptionPanel.querySelector("[data-download-floorplan-pdf]");
        const originalButtonContent = downloadPdfButton?.innerHTML;

        downloadPdfButton?.addEventListener("click", (e) => {
            downloadPdfButton.disabled = true;
            downloadPdfButton.innerHTML = LOADER_SVG;
            const id = downloadPdfButton.getAttribute('data-id');
            const title = downloadPdfButton.getAttribute('data-title');

            let optionIds = [];
            const options = homeFloorplanOptionPanel.querySelectorAll("input[data-floorplan-switch]:checked");
            options.forEach((option) => {
                optionIds.push(option.getAttribute('data-option-id'));
            });

            const url = '/pdf/floorplan-pdf';
            let content = new FormData();
            content.set('homeDesignId', id);
            content.set('optionIds', optionIds.join(","));
            content.set(window.csrfTokenName, window.csrfTokenValue);

            fetch(url, {
                method: 'POST',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'HTTP_X_REQUESTED_WITH': 'XMLHttpRequest',
                    'Accept': 'application/pdf'
                },
                body: content
            })
                .then(resp => resp.blob())
                .then(blob => {
                    const fileUrl = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.style.display = 'none';
                    a.download = `Floorplan - ${title}.pdf`;
                    a.href = fileUrl;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(fileUrl);
                })
                .catch((e) => {
                    console.log("Encountered an error when downloading the floorplan PDF.")
                    console.log(e);
                })
                .finally(() => {
                    downloadPdfButton.disabled = false;
                    downloadPdfButton.innerHTML = originalButtonContent;
                });
        });
    }
}
